import { useEffect, type ReactNode } from 'react'
import { FiExternalLink, FiX } from 'react-icons/fi'
import type { Recipe, SuggestedActivity } from '@domain/models/suggested-activities'
import type { RecommendedActivityTile } from '@presentation/hooks/useRecommendedActivities'

interface RecommendedActivityDetailsModalProps {
  isOpen: boolean
  tile: RecommendedActivityTile | null
  onClose: () => void
  onViewMoreActivities: () => void
}

const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" fill="%23E5E7EB"%3E%3Crect width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239CA3AF" font-size="18"%3ENo Image%3C/text%3E%3C/svg%3E'

function getImage(content: SuggestedActivity | Recipe | null): string {
  if (!content || !content.imageUrl) {
    return PLACEHOLDER_IMAGE
  }
  return content.imageUrl
}

function getTitle(tile: RecommendedActivityTile): string {
  if (!tile.content) {
    return `${tile.category.title} library`
  }
  return tile.content.title
}

function getDescription(content: SuggestedActivity | Recipe | null): ReactNode {
  if (!content) {
    return 'Sign up to explore this activity and more recommendations in PD Buddy.'
  }

  if ('recipeMarkdownDescription' in content) {
    return content.recipeMarkdownDescription
  }

  return content.description
}

function getExternalLink(content: SuggestedActivity | Recipe | null): string | null {
  if (!content || !('externalLinkUrl' in content)) {
    return null
  }

  if (!content.externalLinkUrl) {
    return null
  }

  return content.externalLinkUrl
}

export default function RecommendedActivityDetailsModal({
  isOpen,
  tile,
  onClose,
  onViewMoreActivities,
}: RecommendedActivityDetailsModalProps) {
  useEffect(() => {
    if (!isOpen) {
      return
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen || !tile) {
    return null
  }

  const title = getTitle(tile)
  const image = getImage(tile.content)
  const description = getDescription(tile.content)
  const externalLink = getExternalLink(tile.content)

  return (
    <div className="fixed inset-0 z-60 bg-black/55">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close recommended activity details modal"
        onClick={onClose}
      />

      <div className="relative z-10 flex h-full w-full items-center justify-center p-4 md:p-6">
        <section
          role="dialog"
          aria-modal="true"
          aria-label="Recommended activity details"
          className="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-surface shadow-2xl"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close recommended activity details modal"
            className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-black/35 text-white transition-colors hover:bg-black/50"
          >
            <FiX className="h-6 w-6" />
          </button>

          <div className="max-h-[85vh] overflow-y-auto">
            <img
              src={image}
              alt={title}
              className="h-56 w-full object-cover sm:h-64"
              loading="lazy"
            />

            <div className="px-6 py-6 sm:px-8 sm:py-8">
              <span className="inline-block rounded-full bg-blue-light px-3 py-1 text-xs font-bold tracking-wide text-blue uppercase">
                {tile.category.title}
              </span>

              <h3 className="mt-3 text-2xl font-bold text-text-primary sm:text-3xl">
                {title}
              </h3>

              <p className="mt-4 whitespace-pre-wrap text-base leading-relaxed text-text-primary">
                {description}
              </p>

              <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={onViewMoreActivities}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue/90 sm:text-base"
                >
                  View more activities
                </button>

                {externalLink ? (
                  <a
                    href={externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-blue px-4 py-3 text-sm font-semibold text-blue no-underline transition-colors hover:bg-blue/8 sm:text-base"
                  >
                    Open original resource
                    <FiExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
